import { useState, useEffect, useRef, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  getRuns,
  getRun,
  createRun,
  cancelRun,
  syncRun,
  getLogs,
  getOutputs,
  getConfigs,
  createConfig,
  updateConfig,
  deleteConfig,
  getTenantSession,
} from '@/lib/triplets-api'
import type {
  CreatePipelineRunPayload,
  PipelineConfig,
  LogWsMessage,
} from '@/types/triplets'

// ---------------------------------------------------------------------------
// Pipeline Runs
// ---------------------------------------------------------------------------

export function useRuns(params?: Record<string, string>) {
  return useQuery({
    queryKey: ['triplets-runs', params],
    queryFn: () => getRuns(params),
  })
}

export function useRun(runId: number | string, options?: { refetchInterval?: number | false }) {
  return useQuery({
    queryKey: ['triplets-run', runId],
    queryFn: () => getRun(runId),
    enabled: !!runId,
    refetchInterval: options?.refetchInterval,
  })
}

export function useCreateRun() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: CreatePipelineRunPayload) => createRun(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['triplets-runs'] })
    },
  })
}

export function useCancelRun() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (runId: number | string) => cancelRun(runId),
    onSuccess: (_data, runId) => {
      qc.invalidateQueries({ queryKey: ['triplets-runs'] })
      qc.invalidateQueries({ queryKey: ['triplets-run', runId] })
    },
  })
}

export function useSyncRun() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (runId: number | string) => syncRun(runId),
    onSuccess: (_data, runId) => {
      qc.invalidateQueries({ queryKey: ['triplets-run', runId] })
    },
  })
}

// ---------------------------------------------------------------------------
// Logs — REST polling
// ---------------------------------------------------------------------------

export function useLogs(runId: number | string, options?: { n?: number; refetchInterval?: number | false }) {
  return useQuery({
    queryKey: ['triplets-logs', runId, options?.n],
    queryFn: () => getLogs(runId, options?.n),
    enabled: !!runId,
    refetchInterval: options?.refetchInterval,
  })
}

// ---------------------------------------------------------------------------
// Logs — WebSocket streaming
// ---------------------------------------------------------------------------

export type WsConnectionState = 'connecting' | 'connected' | 'disconnected' | 'error'

const MAX_RECONNECT_ATTEMPTS = 10
const BASE_RECONNECT_DELAY = 1000
const MAX_RECONNECT_DELAY = 30000

export function usePipelineLogs(runId: number | string | undefined) {
  const [connectionState, setConnectionState] = useState<WsConnectionState>('disconnected')
  const [lines, setLines] = useState<string[]>([])
  const [done, setDone] = useState(false)

  const socketRef = useRef<WebSocket | null>(null)
  const reconnectAttemptsRef = useRef(0)
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const cleanup = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
      reconnectTimeoutRef.current = null
    }
    if (socketRef.current) {
      socketRef.current.onopen = null
      socketRef.current.onmessage = null
      socketRef.current.onclose = null
      socketRef.current.onerror = null
      if (
        socketRef.current.readyState === WebSocket.OPEN ||
        socketRef.current.readyState === WebSocket.CONNECTING
      ) {
        socketRef.current.close()
      }
      socketRef.current = null
    }
  }, [])

  const scheduleReconnect = useCallback(() => {
    if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
      setConnectionState('error')
      return
    }
    reconnectAttemptsRef.current++
    const delay =
      Math.min(
        BASE_RECONNECT_DELAY * Math.pow(1.5, reconnectAttemptsRef.current - 1),
        MAX_RECONNECT_DELAY,
      ) *
      (0.8 + Math.random() * 0.4)

    setConnectionState('connecting')
    reconnectTimeoutRef.current = setTimeout(() => {
      reconnectTimeoutRef.current = null
      connectWs()
    }, delay)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleMessage = useCallback((event: MessageEvent) => {
    try {
      const msg: LogWsMessage = JSON.parse(event.data)

      if ('line' in msg) {
        setLines((prev) => [...prev, msg.line])
      } else if ('event' in msg && msg.event === 'done') {
        setDone(true)
      } else if ('event' in msg && msg.event === 'error') {
        setLines((prev) => [...prev, `[ERROR] ${msg.message}`])
      }
    } catch {
      // raw text fallback
      setLines((prev) => [...prev, event.data])
    }
  }, [])

  const connectWs = useCallback(() => {
    if (!runId) return
    cleanup()
    setConnectionState('connecting')

    const wsBaseUrl = import.meta.env.VITE_WS_URL
    let wsUrl: string
    if (wsBaseUrl) {
      wsUrl = `${wsBaseUrl}/ws/triplets/pipelines/${String(runId)}/logs/`
    } else {
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
      const host = window.location.host
      wsUrl = `${protocol}//${host}/ws/triplets/pipelines/${String(runId)}/logs/`
    }

    try {
      const socket = new WebSocket(wsUrl)
      socketRef.current = socket

      socket.onopen = () => {
        reconnectAttemptsRef.current = 0
        setConnectionState('connected')
      }

      socket.onmessage = handleMessage

      socket.onclose = (event) => {
        cleanup()
        setConnectionState('disconnected')
        // Don't reconnect on normal close or if done
        if (event.code !== 1000) {
          scheduleReconnect()
        }
      }

      socket.onerror = () => {
        // onclose fires after onerror
      }
    } catch {
      setConnectionState('disconnected')
      scheduleReconnect()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runId, cleanup, handleMessage, scheduleReconnect])

  useEffect(() => {
    if (runId) {
      setLines([])
      setDone(false)
      connectWs()
    }
    return () => {
      cleanup()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runId])

  return { connectionState, lines, done }
}

// ---------------------------------------------------------------------------
// Outputs
// ---------------------------------------------------------------------------

export function useOutputs(runId: number | string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['triplets-outputs', runId],
    queryFn: () => getOutputs(runId),
    enabled: options?.enabled !== false && !!runId,
  })
}

// ---------------------------------------------------------------------------
// Tenant Session
// ---------------------------------------------------------------------------

export function useTenantSession(tenantName: string | undefined, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['triplets-tenant-session', tenantName],
    queryFn: () => getTenantSession(tenantName!),
    enabled: options?.enabled !== false && !!tenantName,
  })
}

// ---------------------------------------------------------------------------
// Configs
// ---------------------------------------------------------------------------

export function useConfigs() {
  return useQuery({
    queryKey: ['triplets-configs'],
    queryFn: () => getConfigs(),
  })
}

export function useCreateConfig() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Partial<PipelineConfig>) => createConfig(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['triplets-configs'] })
    },
  })
}

export function useUpdateConfig() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<PipelineConfig> }) =>
      updateConfig(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['triplets-configs'] })
    },
  })
}

export function useDeleteConfig() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (configId: number) => deleteConfig(configId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['triplets-configs'] })
    },
  })
}
